
import React, { Fragment, useEffect, useMemo, useState } from 'react'
import { Alert, Col, FormGroup, FormLabel, Row } from 'react-bootstrap'
import { useDispatch, useSelector } from 'react-redux'

import useTranslation from '@/Hooks/UseTranslation'
import { useProgrammation } from '@/Hooks/UseProgrammation'
import { addRelation } from '@/Services/ProgrammationManagerService'
import { getItemsOptionsFromTaskList, getWorksOptionsFromTaskList } from './RelationsHelper'
import { updateRelationsAction } from '@/Redux/Actions/ProgrammationManagerActions'

import Modal from '@/Components/Modal/Modal'
import Button from '@/Components/Button/Button'
import InputSelect from '../../components/InputSelect'
import InputText from '../../components/InputText'

import AddICon from '../../../SvgImages/Add'
import SwapIcon from '@/Components/SvgImages/Swap/Swap'
import RelationTypeSelector from './RelationTypeSelector'

import './RelationsCreator.scss'

const RelationsCreator = () => {

  const { t } = useTranslation()

  const dispatch = useDispatch()

  const { itemOpened, setEditingItem, patchItemOpened } = useProgrammation()

  const macroResult = useSelector((state: any) => state.MacroReducer.result)

  const [ showModal, setShowModal ] = useState(false)
  const [ taskA, setTaskA ] = useState('')
  const [ taskB, setTaskB ] = useState('')
  const [ showCurrentItemError, setShowCurrentItemError ] = useState(false)
  const [ showDuplicationError, setShowDuplicationError ] = useState(false)
  const [ relationType, setRelationType ] = useState('end_to_start')
  const [ lag, setLag ] = useState('0')
  const [ creating, setCreating ] = useState(false)

  const relationsTypes = [
    {
      value: 'end_to_start',
      label: t('RELATIONS_CREATOR:Preset:end_to_start'),
      description: t('RELATIONS_CREATOR:PresetDescription:end_to_start'),
      code: t('RELATIONS_CREATOR:Type:end_to_start'),
    },
    {
      value: 'start_to_start',
      label: t('RELATIONS_CREATOR:Preset:start_to_start'),
      description: t('RELATIONS_CREATOR:PresetDescription:start_to_start'),
      code: t('RELATIONS_CREATOR:Type:start_to_start'),
    },
    {
      value: 'end_to_end',
      label: t('RELATIONS_CREATOR:Preset:end_to_end'),
      description: t('RELATIONS_CREATOR:PresetDescription:end_to_end'),
      code: t('RELATIONS_CREATOR:Type:end_to_end'),
    },
    {
      value: 'start_to_end',
      label: t('RELATIONS_CREATOR:Preset:start_to_end'),
      description: t('RELATIONS_CREATOR:PresetDescription:start_to_end'),
      code: t('RELATIONS_CREATOR:Type:start_to_end'),
    },
  ]

  const tasksOptions = useMemo(() => {

    if (itemOpened.type !== 'trabajo') {

      return getItemsOptionsFromTaskList({
        list: macroResult,
        currentItem: itemOpened,
        currentLabel: t('RELATIONS_CREATOR:CurrentItem'),
        noNameText: t('RELATIONS_CREATOR:Nameless'),
        level: 0,
      })
    } else if (itemOpened.type === 'trabajo') {

      return getWorksOptionsFromTaskList({
        list: macroResult,
        currentWork: itemOpened,
        currentLabel: t('RELATIONS_CREATOR:CurrentWork'),
        noNameText: t('RELATIONS_CREATOR:Nameless'),
        level: 0,
      })
    }
  }, [macroResult, itemOpened])

  const getTaskLabel = (taskId: string) => {

    const taskOption = tasksOptions?.find((option: any) => !option.isGroup && option.value === taskId)

    return taskOption?.label || t('RELATIONS_CREATOR:UnselectedTask')
  }

  const getSelectedTaskLabel = (taskId: string) => {

    const taskOption = tasksOptions?.find((option: any) => !option.isGroup && option.value === taskId)

    return taskOption?.name || taskOption?.label || null
  }

  const getRelationSummary = () => {

    const type = relationsTypes.find(({ value }) => value === relationType)
    const lagValue = Number(lag) || 0
    const lagText = lagValue === 0
      ? t('RELATIONS_CREATOR:SummaryNoLag')
      : t(lagValue > 0 ? 'RELATIONS_CREATOR:SummaryPositiveLag' : 'RELATIONS_CREATOR:SummaryNegativeLag')
          .replace('{{days}}', `${Math.abs(lagValue)}`)

    return t('RELATIONS_CREATOR:Summary')
      .replace('{{from}}', getTaskLabel(taskA))
      .replace('{{type}}', type?.code.toLowerCase() || '')
      .replace('{{to}}', getTaskLabel(taskB))
      .replace('{{lag}}', lagText)
  }

  const handleSwapTasks = () => {

    setTaskA(taskB)
    setTaskB(taskA)
  }

  const adjustLag = (amount: number) => {

    setLag(`${(Number(lag) || 0) + amount}`)
  }

  const handleAddRelation = async () => {

    setCreating(true)
    setEditingItem(true)

    const [ error, data ] = await addRelation(relationType, taskA, taskB, Number(lag) || 0)

    setCreating(false)
    setEditingItem(false)

    if (error) {
      return console.log(error)
    }

    patchItemOpened({
      relations: [...itemOpened.relations, data.relation]
    })

    dispatch(updateRelationsAction(data, true))

    setShowModal(false)

    setTaskA('')
    setTaskB('')
    setRelationType('end_to_start')
    setLag('0')
  }

  const handleCancel = () => {

    setShowModal(false)
    setTaskA('')
    setTaskB('')
    setRelationType('end_to_start')
    setLag('0')
    setShowCurrentItemError(false)
  }

  useEffect(() => {

    if (!tasksOptions || !showModal) return

    console.log('tasksOptions', tasksOptions)

    setTaskA(itemOpened._id)
  }, [tasksOptions, showModal])

  useEffect(() => {

    const validateRelation = () => {

      const areBothTasksSelected = taskA && taskB

      const taskAIsCurrentItem = taskA === itemOpened._id
      const taskBIsCurrentItem = taskB === itemOpened._id

      const currentIsSelected = taskAIsCurrentItem || taskBIsCurrentItem

      if (!areBothTasksSelected) return

      if (taskA === taskB && currentIsSelected) {

        setShowDuplicationError(true)
        return
      }

      if (!taskAIsCurrentItem && !taskBIsCurrentItem) {

        console.log('no ha sido seleccionado', taskA, taskB, itemOpened._id)

        setShowCurrentItemError(true)
      }
    }

    setShowCurrentItemError(false)
    setShowDuplicationError(false)

    validateRelation()

    return () => {

      setShowCurrentItemError(false)
      setShowDuplicationError(false)
    }
  }, [taskA, taskB])

  return (
    <Fragment>
      <Button
        variant="primary"
        onClick={() => setShowModal(true)}
        className='mb-2'
      >
        <AddICon fill="#fff" height={24} width={24} />
      </Button>

      <Modal
        show={showModal}
        title={t('RELATIONS_CREATOR:Title')}
        onClose={handleCancel}
        backdropClassName='relations-creator-backdrop'
        className='relations-creator'
        showCloseButton={true}
        scrollable
        zIndex='2'
      >
        <div className="relation-task-pair">
          <FormGroup className="relation-task-field">
            <FormLabel>{t('RELATIONS_CREATOR:OriginTask')}</FormLabel>
            <InputSelect
              placeholder={itemOpened.type === 'trabajo' ? t('RELATIONS_CREATOR:SelectAWork') : t('RELATIONS_CREATOR:SelectAnItem')}
              value={taskA}
              options={tasksOptions}
              onChange={setTaskA}
              renderOption={(option: any) => (
                <>
                  {option.isGroup && (
                    <optgroup key={option.value} label={option.label}></optgroup>
                  )}
                  {!option.isGroup && (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  )}
                </>
              )}
            />
          </FormGroup>
          <button
            type="button"
            className="relation-swap-button"
            onClick={handleSwapTasks}
            disabled={!taskA && !taskB}
            title={t('RELATIONS_CREATOR:SwapTasks')}
            aria-label={t('RELATIONS_CREATOR:SwapTasks')}
          >
            <SwapIcon height={18} width={18} />
          </button>
          <FormGroup className="relation-task-field">
            <FormLabel>{t('RELATIONS_CREATOR:DestinationTask')}</FormLabel>
            <InputSelect
              placeholder={itemOpened.type === 'trabajo' ? t('RELATIONS_CREATOR:SelectAWork') : t('RELATIONS_CREATOR:SelectAnItem')}
              value={taskB}
              options={tasksOptions}
              onChange={setTaskB}
              renderOption={(option: any) => (
                <>
                  {option.isGroup && (
                    <optgroup key={option.value} label={option.label}></optgroup>
                  )}
                  {!option.isGroup && (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  )}
                </>
              )}
            />
          </FormGroup>
        </div>

        <FormGroup>
          <FormLabel>{t('RELATIONS_CREATOR:Behavior')}</FormLabel>
          <RelationTypeSelector
            value={relationType}
            options={relationsTypes}
            originName={getSelectedTaskLabel(taskA)}
            destinationName={getSelectedTaskLabel(taskB)}
            onChange={setRelationType}
          />
        </FormGroup>

        <FormGroup>
          <FormLabel>{t('RELATIONS_CREATOR:LagDays')}</FormLabel>
          <div className="relation-lag-control">
            <button
              type="button"
              onClick={() => adjustLag(-1)}
              disabled={creating}
              aria-label={t('RELATIONS_CREATOR:DecreaseLag')}
              title={t('RELATIONS_CREATOR:DecreaseLag')}
            >
              −
            </button>
            <InputText
              type="number"
              className="relation-lag-input"
              value={lag}
              onChange={setLag}
              delay={0}
              disabled={creating}
            />
            <button
              type="button"
              onClick={() => adjustLag(1)}
              disabled={creating}
              aria-label={t('RELATIONS_CREATOR:IncreaseLag')}
              title={t('RELATIONS_CREATOR:IncreaseLag')}
            >
              +
            </button>
            <span>{t('RELATIONS_CREATOR:WorkingDays')}</span>
          </div>
        </FormGroup>

        <div className="relation-summary" aria-live="polite">
          {getRelationSummary()}
        </div>
        {showCurrentItemError && (
          <Row>
            <Col>
              <Alert variant="danger">
                {t('RELATIONS_CREATOR:CurrentItemRequired').replace('{{task}}', itemOpened.name)}
              </Alert>
            </Col>
          </Row>
        )}
        {showDuplicationError && (
          <Row>
            <Col>
              <Alert variant="danger">
                {t('RELATIONS_CREATOR:DuplicateTask').replace('{{task}}', itemOpened.name)}
              </Alert>
            </Col>
          </Row>
        )}
        <Row>
          <Col style={{ textAlign: 'right' }}>
            <Button
              variant='primary'
              onClick={() => handleAddRelation()}
              disabled={!taskA || !taskB || showCurrentItemError || showDuplicationError}
            >
              <AddICon fill="#fff" height={24} width={24} />{' '}
              {!creating && t('RELATIONS_CREATOR:AddButton')}
              {creating && t('RELATIONS_CREATOR:AddButtonAdding')}
            </Button>
          </Col>
        </Row>
      </Modal>
    </Fragment>
  )
}

export default RelationsCreator
